import React, { type ComponentProps, ReactNode, useMemo } from 'react';

import { CSSProp } from 'styled-components';

import { useMultiDropdown } from '../hooks';
import { MultiControl } from '../layout';
import { SearchInputProps } from '../layout/Control/SearchInput';
import type {
  GenericItem,
  UseMultiDropdownProps,
  VirtualProps,
  RenderGroupHeaderProps,
} from '../types';
import { GetSelectedItemProps, ItemToString, RenderMenuItem } from '../types';
import DropdownMenu from './Menu/DropdownMenu';
import { filterUndefined } from '../helpers/filterUndefined';
import Placeholder from '../layout/Control/Placeholder';

type ControlProps<TItem extends GenericItem> = ComponentProps<
  typeof MultiControl<TItem>
>;

type MenuProps<TItem extends GenericItem> = ComponentProps<
  typeof DropdownMenu<TItem>
>;

export type MultiDropdownTemplateProps<TItem extends GenericItem> =
  UseMultiDropdownProps<TItem> & {
    style?: CSSProp;
    /**
     * Disable the entire dropdown
     */
    disabled?: boolean;
    /**
     * Enable error state
     */
    error?: boolean;
    /**
     * Enable loading state
     */
    isLoading?: boolean;
    /**
     * Placeholder text to display
     */
    placeholder?: string;
    /**
     * Function to render a custom dropdown control
     * @param args Props to be provided to the control
     * @returns A dropdown control element
     */
    renderControl?: (args: ControlProps<TItem>) => React.ReactNode;
    /**
     * Function to render a custom dropdown menu
     * @param args Props to be provided to the menu
     * @param virtualProps Props used to virtualize the menu - see stories for examples
     * @returns A dropdown menu element
     */
    renderMenu?: (
      args: MenuProps<TItem>,
      virtualProps: VirtualProps,
    ) => React.ReactNode;
    /**
     * Function to render a custom input element
     */
    renderInput?: (props: SearchInputProps) => ReactNode;
    /**
     * Function to render custom selected items
     * @param isOpen Whether the dropdown is open
     * @param selectedItems The selected items
     * @param getSelectedItemProps Function to get props for a selected item
     * @param removeSelectedItem Function to remove a selected item
     * @param itemToString Function to convert an item to a string
     * @param disabled Whether the dropdown is disabled
     */
    renderItems?: ({
      isOpen,
      selectedItems,
      getSelectedItemProps,
      removeSelectedItem,
      itemToString,
      disabled,
    }: {
      getSelectedItemProps: GetSelectedItemProps<TItem>;
      isOpen: boolean;
      selectedItems: TItem[];
      removeSelectedItem: (item: TItem) => void;
      itemToString: ItemToString<TItem>;
      disabled?: boolean;
    }) => ReactNode;
    /**
     * Function to render a custom placeholder element
     * @param props Props to be provided to the placeholder
     * @returns A placeholder element
     */
    renderPlaceholder?: (
      props?: ComponentProps<typeof Placeholder>,
    ) => ReactNode;
    /**
     * Function to render a custom menu item
     */
    renderMenuItem?: RenderMenuItem<TItem>;
    /**
     * Function to render a custom group header menu item
     */
    renderGroupHeader?: (props: RenderGroupHeaderProps) => ReactNode;
  };

const defaultRenderControl = <TItem extends GenericItem>(
  args: ControlProps<TItem>,
) => <MultiControl {...args} />;

/**
 * A multi select, non-searchable dropdown component.
 *
 * This component covers the majority of use cases, but advanced use cases may require using the `useMultiDropdown` hook directly.
 */
const Dropdown = <TItem extends GenericItem>({
  style,
  disabled,
  error,
  isLoading,
  placeholder,
  renderControl = defaultRenderControl,
  renderMenu = DropdownMenu.render,
  renderItems,
  renderInput,
  renderPlaceholder,
  renderMenuItem,
  renderGroupHeader,
  ...args
}: MultiDropdownTemplateProps<TItem>) => {
  const { controlProps, menuProps, virtualProps } = useMultiDropdown(args);

  // Filter out undefined props to ensure they are not passed to the control
  // as explicitly undefined and overwrite other props
  const filteredControlProps = useMemo(
    () =>
      filterUndefined({
        disabled,
        error,
        isLoading,
        placeholder,
        renderItems,
        renderInput,
        renderPlaceholder,
      }),
    [
      disabled,
      error,
      isLoading,
      placeholder,
      renderInput,
      renderItems,
      renderPlaceholder,
    ],
  );
  const filteredMenuProps = useMemo(
    () => filterUndefined({ renderMenuItem, renderGroupHeader }),
    [renderMenuItem, renderGroupHeader],
  );

  return (
    <div css={[{ width: 300 }, style]}>
      {renderControl({
        ...filteredControlProps,
        ...controlProps,
      })}
      {renderMenu({ ...menuProps, ...filteredMenuProps }, virtualProps)}
    </div>
  );
};

export default Dropdown;
